/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { gql } from '@apollo/client';

export const CREATE_DEPARTMENT = gql`
  mutation CreateDepartment($input: CreateDepartmentInput!) {
    createDepartment(input: $input) {
      id
      owner
      organizationId
      name
      draftKnowledgeId
      publishedKnowledgeId
      workerId
      local
      state
      errors
      createdAt
      isDeleted
      brainVersion
      features
      organization {
        id
        name
      }
      draftKnowledge {
        id
        brainVersion
      }
    }
  }
`;

export const UPGRADE_DEPARTMENT = gql`
  mutation UpgradeDepartment($departmentId: ID!, $brainVersion: String) {
    upgradeDepartment(
      departmentId: $departmentId
      brainVersion: $brainVersion
    ) {
      id
      owner
      organizationId
      name
      draftKnowledgeId
      publishedKnowledgeId
      workerId
      local
      state
      errors
      createdAt
      isDeleted
      brainVersion
      features
      organization {
        id
        name
      }
      draftKnowledge {
        id
        brainVersion
      }
    }
  }
`;

export const ROLLBACK_DEPARTMENT = gql`
  mutation RollbackDepartment($departmentId: ID!) {
    rollbackDepartment(departmentId: $departmentId) {
      id
      owner
      organizationId
      name
      draftKnowledgeId
      publishedKnowledgeId
      workerId
      local
      state
      errors
      createdAt
      isDeleted
      brainVersion
      features
      organization {
        id
        name
      }
      draftKnowledge {
        id
        brainVersion
      }
    }
  }
`;

export const REMOVE_USER_FROM_DEPARTMENT = gql`
  mutation RemoveUserFromDepartment($departmentId: ID!, $userId: ID!) {
    removeUserFromDepartment(departmentId: $departmentId, userId: $userId) {
      status
    }
  }
`;

export const ROLLBACK_DEPARTMENT_VERSION = gql`
  mutation RollbackDepartmentVersion($input: DepartmentRollbackInput!) {
    rollbackDepartmentVersion(input: $input) {
      id
      departmentId
      previousVersion
      currentVersion
      rollbackVersion
    }
  }
`;

export const DELETE_DEPARTMENT = gql`
  mutation DeleteDepartment($input: DeleteDepartmentInput!) {
    deleteDepartment(input: $input) {
      id
      owner
      organizationId
      readers
      editors
      name
      draftKnowledgeId
      publishedKnowledgeId
      workerId
      local
      state
      errors
      createdAt
      isDeleted
      upgradeCommandId
      notifyProcedureInvocation
      features
      brainVersion
      status
      purgeWorkerDataAfter
      provisionedConcurrency
      worker {
        id
        owner
        readers
        editors
        name
        description
        version
        departmentVersion
        knowledgeId
        departmentId
        procedureId
        entryCommand
        entryCommandId
        originalWorkerId
        createdAt
        state
        stateLastUpdatedAt
        status
        statusLastUpdatedAt
        attachments
        isArchived
        features
      }
      organization {
        id
        name
        owner
        createdAt
        updatedAt
        billingStatus
      }
      draftKnowledge {
        id
        owner
        readers
        editors
        name
        description
        departmentId
        workerId
        parentKnowledgeId
        state
        brainVersion
      }
      publishedKnowledge {
        id
        owner
        readers
        editors
        name
        description
        departmentId
        workerId
        parentKnowledgeId
        state
        brainVersion
      }
      requests {
        limit
        nextToken
      }
      learnedAnswers {
        nextToken
      }
    }
  }
`;

export const UPDATE_DEPARTMENT = gql`
  mutation UpdateDepartment($input: UpdateDepartmentInput!) {
    updateDepartment(input: $input) {
      id
      owner
      organizationId
      readers
      editors
      name
      draftKnowledgeId
      publishedKnowledgeId
      workerId
      local
      state
      errors
      createdAt
      isDeleted
      upgradeCommandId
      notifyProcedureInvocation
      features
      brainVersion
      status
      purgeWorkerDataAfter
      provisionedConcurrency
      worker {
        id
        owner
        readers
        editors
        name
        description
        version
        departmentVersion
        knowledgeId
        departmentId
        procedureId
        entryCommand
        entryCommandId
        originalWorkerId
        createdAt
        state
        stateLastUpdatedAt
        status
        statusLastUpdatedAt
        attachments
        isArchived
        features
      }
      organization {
        id
        name
        owner
        createdAt
        updatedAt
        billingStatus
      }
      draftKnowledge {
        id
        owner
        readers
        editors
        name
        description
        departmentId
        workerId
        parentKnowledgeId
        state
        brainVersion
      }
      publishedKnowledge {
        id
        owner
        readers
        editors
        name
        description
        departmentId
        workerId
        parentKnowledgeId
        state
        brainVersion
      }
      requests {
        limit
        nextToken
      }
      learnedAnswers {
        nextToken
      }
    }
  }
`;

export const IMPORT_DEPARTMENT = gql`
  mutation ImportDepartment($input: DepartmentImportInput!) {
    importDepartment(input: $input) {
      id
      owner
      organizationId
      name
      draftKnowledgeId
      publishedKnowledgeId
      workerId
      local
      state
      errors
      createdAt
      isDeleted
      brainVersion
      features
      organization {
        id
        name
      }
      draftKnowledge {
        id
        brainVersion
      }
    }
  }
`;

export const UPSERT_USER_TO_DEPARTMENT = gql`
  mutation UpsertUserToDepartment(
    $departmentId: ID!
    $userId: ID!
    $role: AccessControlRole!
  ) {
    upsertUserToDepartment(
      departmentId: $departmentId
      userId: $userId
      role: $role
    ) {
      status
    }
  }
`;

export const PUBLISH_DEPARTMENT = gql`
  mutation PublishDepartment($input: PublishDepartmentInput!) {
    publishDepartment(input: $input) {
      id
      departmentId
      departmentVersion
      publishedAt
      publishedBy
    }
  }
`;
